import _extends from "@babel/runtime/helpers/extends";
import _get from "lodash/get";
import "core-js/modules/es6.string.link.js";
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import pick from 'lodash/pick';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import values from 'lodash/values';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import defaultsDeep from 'lodash/defaultsDeep';
import { Click } from '@nerdwallet/nw-client-lib/tracking';
import LinkType from './link-type';
import FinishType from './finish-type';
import Monetizable from './monetizable';

/**
 * Returns a new object which contains props required for the `<Click />`
 * component, out of props picked from the supplied argument.
 *
 * This includes properties required for both the Click event as well as
 * BetaProductShoppingFinish event.
 *
 * @example
 * ```
 * // Use in a component like so
 * SomeComponent = props => <Click {...getClickProps(props)} />
 *
 * // The structure is as follows
 * {
 *   clickProp1,
 *   clickProp2,
 *   eventProps: {
 *     // properties which will be inserted into the apply-now URL query-string.
 *     // both for Click event and BetaProductShoppingFinish.
 *     // we will transform keys to snake case as appropriate.
 *   }
 * }
 * ```
 *
 * @param {Object} props -
 * @returns {Object} - Props for <Click />.
 */
var getClickProps = function getClickProps(props) {
  var additionalAttributes = props.additionalAttributes,
    ext_click_id = props.ext_click_id,
    finishType = props.finishType,
    hasPrequalified = props.hasPrequalified,
    impressionId = props.impressionId,
    link = props.link,
    linkType = props.linkType,
    monetizable = props.monetizable,
    pageNumber = props.pageNumber,
    productDisplayDriver = props.productDisplayDriver,
    productImpressionId = props.productImpressionId,
    productPosition = props.productPosition,
    referringUrl = props.referringUrl,
    sectionName = props.sectionName,
    sectionPosition = props.sectionPosition,
    shortButton = props.shortButton,
    prequalId = props.prequalId;
  var clickProps = pick(props, [
  // Props expected by tracking/Click
  'eventProps', 'pageViewId',
  // General purpose props
  'className', 'component', 'disabled', 'id', 'rel', 'primary', 'secondary', 'tertiary', 'newWindow',
  // eslint-disable-next-line camelcase
  ext_click_id ? 'ext_click_id' : '' // Used to identify performance marketing campaigns
  ]);

  clickProps.short = shortButton;
  clickProps.href = link;
  if (referringUrl) {
    clickProps.href = "".concat(link, "&url=").concat(referringUrl);
    clickProps.rel = 'nofollow noreferrer noopener';
  }
  var clickHeaderProps = pick(props, ['pageNumber', 'productPosition', 'productLocation', 'productInstance', 'sectionPosition', 'productId', 'productSlug', 'linkType']);
  clickProps.eventProps = defaultsDeep(clickProps.eventProps || {}, {
    clickHeader: clickHeaderProps
  });

  /* Following events are required for the new flex event (BetaProductShoppingFinish)
     See https://github.com/NerdWallet/schemas/blob/master/protobuf/src/event/BetaProductShoppingFinish.proto#L51 */
  clickProps.eventProps = defaultsDeep(clickProps.eventProps || {}, {
    finish_type: finishType,
    has_prequalified: hasPrequalified,
    impression_id: impressionId,
    link_type: linkType,
    monetizable: monetizable,
    page_number: pageNumber,
    product_impression_id: productImpressionId,
    product_display_driver: productDisplayDriver,
    product_position: productPosition,
    section_name: sectionName,
    section_position: sectionPosition
  });
  if (prequalId) {
    clickProps.eventProps.prequal_id = prequalId;
  }

  // eslint-disable-next-line camelcase
  if (ext_click_id) {
    // eslint-disable-next-line camelcase
    clickProps.eventProps.ext_click_id = ext_click_id;
  }
  if (additionalAttributes) {
    clickProps.eventProps.additional_attributes = JSON.stringify(additionalAttributes);
  }
  return clickProps;
};

/**
 * Picks props required for `<Impression />` and returns a new object which
 * can be spread in JSX.
 *
 * @example
 * ```
 * SomeComponent = props => <Impression {...getImpressionProps(props)} />
 * ```
 * @param {Object} props -
 * @returns {Object} - props required for `<Impression />`.
 */

var ApplyNowLink = function ApplyNowLink(props) {
  var children = props.children,
    onClick = props.onClick;

  // Mimics how nwa appends `source_url` urls during onMouseDown or onClick events.
  // See https://github.com/NerdWallet/nwa/blob/7d439d3ea975b34267ff9a3b970c89c8a36130b2/src/browser/reporters/base-click.js#L75
  var appendSourceUrl = useCallback(function (e) {
    if (!e) return;
    if (typeof window === 'undefined') return;
    var href = e.currentTarget.getAttribute('href');
    if (!href) return;

    // if a source_url is already present, don't append another one
    if (/source_url/g.test(href)) return;

    // augment the href with the source_url
    var sourceUrl = window.location.href;
    var queryPrefix = href.indexOf('?') > -1 ? '&' : '?';
    var newHref = "".concat(href).concat(queryPrefix, "source_url=").concat(encodeURIComponent(sourceUrl));
    e.currentTarget.setAttribute('href', newHref);
  }, []);
  var handleClick = useCallback(function (e) {
    appendSourceUrl(e);
    onClick === null || onClick === void 0 ? void 0 : onClick(e);
  }, [onClick, appendSourceUrl]);
  var handleMouseDown = useCallback(function (e) {
    appendSourceUrl(e);
  }, [appendSourceUrl]);
  return /*#__PURE__*/React.createElement(Click, _extends({
    onClick: handleClick,
    onMouseDown: handleMouseDown
  }, getClickProps(props)), children);
};
ApplyNowLink.defaultProps = {
  additionalAttributes: null,
  children: null,
  className: null,
  component: null,
  ext_click_id: null,
  finishType: FinishType.EXTERNAL_APPLICATION,
  hasPrequalified: false,
  impressionId: null,
  isCC: false,
  linkType: null,
  monetizable: null,
  newWindow: true,
  onClick: function onClick() {},
  pageNumber: null,
  prequalId: null,
  productDisplayDriver: null,
  productId: null,
  productImpressionId: null,
  productPosition: null,
  referringUrl: null,
  rel: 'nofollow',
  sectionName: null,
  sectionPosition: null,
  shortButton: false,
  vertical: _get(global, 'window.nwPageTaxonomy.vertical')
};

/* eslint-disable  react/forbid-prop-types */
ApplyNowLink.propTypes = {
  // An object containing extra properties for BetaProductShoppingFinish (which has a field called additional_attributes).
  additionalAttributes: PropTypes.object,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  className: PropTypes.string,
  component: PropTypes.any,
  // BetaProductShoppingFinish:finish_type
  ext_click_id: PropTypes.string,
  finishType: PropTypes.oneOf(values(FinishType)),
  hasPrequalified: PropTypes.bool,
  // Used for impression identification. If there are multiple apply nows for the same product on a page, this should be unique for each one.
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  impressionId: PropTypes.string,
  // CC (credit-cards vertical) uses a cc-specific impression event. Set this to true for that behavior.
  isCC: PropTypes.bool,
  // The apply now link. obtained from the marketplace object.
  link: PropTypes.string.isRequired,
  // What type of control was the link on? See link-type.js for more info.
  linkType: PropTypes.oneOf(values(LinkType)),
  // Is this a monetizable link?
  monetizable: PropTypes.oneOf(values(Monetizable)),
  // If link should open in new window, this is true by default
  newWindow: PropTypes.bool,
  onClick: PropTypes.func,
  // For a multi-page interface (like cc tool), which page did this card appear on?
  pageNumber: PropTypes.number,
  prequalId: PropTypes.string,
  // Why was this card displayed to the user?
  productDisplayDriver: PropTypes.string,
  // The impression_id of the product impression that the user is selecting
  productImpressionId: PropTypes.string,
  // The ID of a financial product
  productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // For lists of card. Which position was this card at?
  productPosition: PropTypes.number,
  referringUrl: PropTypes.string,
  rel: PropTypes.string,
  // For a section interface (like BCC)
  sectionName: PropTypes.string,
  // Where was the section located on the page
  sectionPosition: PropTypes.number,
  // Renders a short button
  shortButton: PropTypes.bool,
  // The vertical to be used by the product impression
  vertical: PropTypes.string
};
/* eslint-enable */

export default ApplyNowLink;